/* eslint-disable react/jsx-props-no-spreading */
/**
 * @fileoverview Lists customer call history in the Inbox details accordion and CRM tab.
 * @module CustomerCallsMC
 */
import React, { memo, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ButtonComponent from '../../../components/ButtonMC';
import LoadingMC from '../../../components/LoadingMC';
import useCustomerCallsData from '../../../hooks/inbox/data/useCustomerCallsData';
import { SIDEBAR_ACTIONS } from '../../../../constants/actionsConstants';
import { useTraceUpdate } from '../../../hooks/shared/useDebugRenders';
import CustomerCallCardMC from './CustomerCallCardMC';

const CustomerCallsMC = memo(({
  customer,
  copyNamespace = 'InboxMC',
  copyPrefix = 'customerDetails.accordion.calls'
}) => {
  const { t } = useTranslation('CallEvent');
  const { t: tCopy } = useTranslation(copyNamespace);
  const dispatch = useDispatch();
  const {
    calls,
    loading,
    error,
    pagination,
    loadMoreCalls,
    reload
  } = useCustomerCallsData(customer);

  useTraceUpdate('CustomerCallsMC', {
    customerId: customer?.id,
    callsCount: calls.length,
    loading,
    hasNextPage: pagination.hasNextPage,
    hasError: Boolean(error)
  }, {
    logRenderCount: true,
    logPropChanges: true
  });

  const openViewCallSidebar = useCallback((call) => {
    dispatch({
      type: SIDEBAR_ACTIONS.OPEN_SIDEBAR,
      payload: {
        componentKey: 'ViewCallSidebar',
        props: { call }
      }
    });
  }, [dispatch]);

  const toggleButtonConfig = useMemo(() => ({
    outlined: true,
    size: 'xs',
    variant: 'SECONDARY',
    className: 'tw-border-0 tw-bg-transparent hover:tw-bg-transparent tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 tw-px-2 tw-py-1',
    iconClassName: '!tw-w-4 !tw-h-4 tw-flex-shrink-0 tw-text-blue-500'
  }), []);

  const showInitialLoading = loading && calls.length === 0 && !error;

  return (
    <div className="tw-wrapper tw-w-full tw-flex tw-flex-col tw-gap-2 tw-mt-4">
      <LoadingMC loading={showInitialLoading} />
      {error && (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-2 tw-py-2">
          <p className="tw-text-xs tw-text-red-600 tw-text-center">
            {tCopy(`${copyPrefix}.error`)}
          </p>
          <ButtonComponent
            outlined
            size="xs"
            variant="SECONDARY"
            text={tCopy(`${copyPrefix}.retry`)}
            onClick={reload}
          />
        </div>
      )}
      {!error && !showInitialLoading && calls.length === 0 && (
        <p className="tw-text-xs tw-text-gray-500 tw-text-center tw-py-2">
          {tCopy(`${copyPrefix}.empty`)}
        </p>
      )}
      {calls.length > 0 && (
        <div className="tw-w-full tw-flex tw-flex-col tw-gap-2">
          {calls.map((call) => (
            <CustomerCallCardMC
              key={`customer-call-${call.id}`}
              call={call}
              onView={openViewCallSidebar}
              tCallEvent={t}
              tCopy={tCopy}
              copyPrefix={copyPrefix}
            />
          ))}
        </div>
      )}
      {pagination.hasNextPage && (
        <div className="tw-flex tw-justify-center tw-mb-2">
          <ButtonComponent
            {...toggleButtonConfig}
            text={loading
              ? tCopy(`${copyPrefix}.loading`)
              : tCopy(`${copyPrefix}.showMore`)}
            icon="arrow-ios-downward-outline"
            onClick={loadMoreCalls}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
});

CustomerCallsMC.displayName = 'CustomerCallsMC';

export default CustomerCallsMC;
